import { severityLabel as axisSeverityLabel } from "../AxisPropertiesFields";
import { colorForValue } from "../colorScaleDisplay";
import type { LidarClearanceLabel } from "../labelsApi";
import type { LidarClearanceAxis, LidarClearanceColorScale, LidarType } from "../schemas";

// axis_id -> axis_value -> label_name, for labels that curate exactly one
// axis value (see apps/global-api's _single_axis_label_names) - what the
// severity/level dropdowns show instead of a bare number, since the selected
// system's own named labels are the more meaningful way to pick a severity.
export type LabelNamesByAxis = Map<number, Map<number, string>>;

export function labelNamesByAxis(labels: LidarClearanceLabel[]): LabelNamesByAxis {
  const byAxis: LabelNamesByAxis = new Map();
  for (const label of labels) {
    if (!label.label_name || label.axis_values.length !== 1) continue;
    const { axis_id: axisId, axis_value: axisValue } = label.axis_values[0];
    if (!byAxis.has(axisId)) byAxis.set(axisId, new Map());
    byAxis.get(axisId)!.set(axisValue, label.label_name);
  }
  return byAxis;
}

export function axisValueName(
  labelNames: LabelNamesByAxis,
  axisId: number,
  value: number
): string | undefined {
  return labelNames.get(axisId)?.get(value);
}

export type ValueOption = {
  value: number;
  name?: string;
  color?: string;
  severityLabel?: string;
};

// `scale` is the axis's effective color scale (see colorScaleDisplay's
// effectiveColorScale).
export function axisValueOption(
  labelNames: LabelNamesByAxis,
  axis: LidarClearanceAxis,
  value: number,
  scale?: LidarClearanceColorScale
): ValueOption {
  return {
    value,
    name: axisValueName(labelNames, axis.id, value),
    color: colorForValue(scale, value),
    severityLabel: axisSeverityLabel(axis, value),
  };
}

export function axisValueOptions(
  labelNames: LabelNamesByAxis,
  axis: LidarClearanceAxis,
  scale?: LidarClearanceColorScale
): ValueOption[] {
  return Array.from({ length: axis.max_value }, (_, index) => index + 1).map((value) =>
    axisValueOption(labelNames, axis, value, scale)
  );
}

// name+value only, never the color swatch or severity caption - keeps a
// value's announced/matched name independent of that presentational context.
export function valueOptionAriaLabel(option: ValueOption): string {
  return option.name ? `${option.name} ${option.value}` : String(option.value);
}

// classification's own options come from the region's lidar_type table
// rather than an axis's value range, so there's no severity to attach.
export function classificationOptions(lidarTypes: LidarType[]): ValueOption[] {
  return lidarTypes.map((lidarType) => ({
    value: lidarType.id,
    name: lidarType.name,
    color: lidarType.color,
  }));
}
